import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { IconButton, SxProps, TextField } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { DatePickerProps, DatePicker as MuiDatePicker } from '@mui/x-date-pickers'
import { Moment } from 'moment'
import { makeStyles } from 'tss-react/mui'
import { B4Calendar } from '@e3dc-react/icons'
import { DayMonthDateFormat } from 'src/Libs/dynamic-tariff-settings/constants'
import { ValidationMessageKeys } from 'src/Configuration/validation/translation-keys'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

const useStyles = makeStyles()(theme => ({
  icon: {
    '& .MuiSvgIcon-root': {
      color: theme.palette.grey.grey00,
      cursor: 'pointer',
    },
  },
}))

type Props = {
  name: string
  onTransform?: (value: Moment | null) => Moment | Date | null
  textFieldStyles?: SxProps<Theme>
} & Omit<Partial<DatePickerProps<Moment, Moment>>, 'name' | 'type'>

/**
 * MUI DatePicker with react hook form
 */
const DatePicker: React.FC<Props> = ({ name, onTransform, textFieldStyles, ...props }) => {
  const { control, setError, clearErrors } = useFormContext()
  const [isOpen, setIsOpen] = useState(false)
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  const invalidDateFormatMessage = t(ValidationMessageKeys.format)

  const toggleOpen = (): void => setIsOpen(prev => !prev)
  const handleClose = (): void => setIsOpen(false)

  const inputProps = {
    endAdornment: (
      <IconButton onClick={toggleOpen} className={classes.icon}>
        <B4Calendar />
      </IconButton>
    ),
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, ...field }, fieldState: { error } }) => (
        <MuiDatePicker
          {...field}
          {...props}
          onError={reason => {
            /* add custom error message for invalid date */
            if (reason === 'invalidDate') {
              setError(name, { message: invalidDateFormatMessage, type: 'onChange' })
            }
          }}
          onChange={value => {
            /* Clear error message if date is valid */
            if (value?.isValid()) {
              clearErrors(name)
            }
            const transformedValue = onTransform?.(value) ?? value
            onChange(transformedValue)
          }}
          renderInput={params => (
            <TextField
              {...params}
              error={!!error}
              helperText={error?.message}
              InputProps={inputProps}
              placeholder={DayMonthDateFormat}
              sx={textFieldStyles}
            />
          )}
          onClose={handleClose}
          open={isOpen}
        />
      )}
    />
  )
}

DatePicker.displayName = 'DatePicker'

export default DatePicker
